import type { FlexProps } from "@qws-org/ui-kit";
import { Box, Button, Flex, Image, Text, Title } from "@qws-org/ui-kit";
import type { FC } from "react";
import React from "react";

import type { Action, Pages, TextAndImageType } from "~/core";
import { isExternalLink, isInternalLink } from "~/core/utils/action.utils";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

type TextAndImageProps = {
  data: TextAndImageType;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
  isControlInactive: (control: Action) => boolean;
} & FlexProps;

export const TextAndImage: FC<TextAndImageProps> = ({
  data,
  onCallAction,
  isControlInactive,
  getUrl,
  ...rest
}) => {
  const { getDirectusImage } = useDirectus();
  const flexDirection =
    data.variant === "vertical"
      ? "column"
      : data.variant === "rightToLeft"
        ? "row-reverse"
        : "row";
  const mobileFlexDirection =
    data.mobile_image_position === "top" ? "column-reverse" : "column";

  const mobileImage = data.picture?.image;
  const mobileColor = data.picture?.color ?? undefined;
  const mobileWidth = data.picture?.mobile_image_width ?? undefined;
  const mobileHeight = data.picture?.mobile_image_height ?? undefined;

  const tabletImage = data.picture?.tablet_image ?? mobileImage;
  const tabletColor = data.picture?.tablet_color ?? mobileColor;
  const tabletWidth = data.picture?.tablet_image_width ?? mobileWidth;
  const tabletHeight = data.picture?.tablet_image_height ?? mobileHeight;

  const desktopImage = data.picture?.desktop_image ?? tabletImage;
  const desktopColor = data.picture?.desktop_color ?? tabletColor;
  const desktopWidth = data.picture?.desktop_image_width ?? tabletWidth;
  const desktopHeight = data.picture?.desktop_image_height ?? tabletHeight;

  const controlAction = data?.action;
  const action = data?.action?.action?.[0];
  const isInactive = controlAction ? isControlInactive(controlAction) : true;

  let href;
  if (isExternalLink(action?.item)) {
    href = action.item.href ?? undefined;
  }
  if (isInternalLink(action?.item)) {
    href = action.item.page ? getUrl(action.item.page) : undefined;
  }
  const handlePressButton = (): void => {
    if (controlAction) {
      onCallAction(controlAction);
    }
  };

  return (
    <Flex
      gap={0}
      align="center"
      flexDirection={mobileFlexDirection}
      {...rest}
      md={{ flexDirection, gap: 40, justify: "space-between", ...rest.md }}
    >
      <Box
        className="md:flex-1"
        md={{
          width:
            !data.image?.id || data.variant === "vertical" ? "100%" : "50%",
        }}
      >
        {data.title && (
          <Title
            as="h3"
            size="medium"
            fontWeight="mobile.semibold"
            m={{ mx: 0, mt: 0, mb: 24 }}
            md={{
              fontWeight: "desktop.bold",
            }}
          >
            {data.title}
          </Title>
        )}
        {data.text && <Text size="medium" m={{ mb: 16 }} content={data.text} />}
        {!isInactive && (
          <Button
            href={href}
            onPress={handlePressButton}
            size="small"
            fontWeight="mobile.bold"
            p={{ py: 12, px: 24 }}
            maxWidth={240}
            horizontalAlign="center"
            width="max-content"
            height="fit-content"
            whiteSpace="pre-wrap"
          >
            {data.action_text}
          </Button>
        )}
      </Box>

      {mobileImage && (
        <Box
          className="md:flex-1"
          md={{
            width:
              !data.image?.id || data.variant === "vertical" ? "100%" : "50%",
          }}
        >
          <Image
            overflow="hidden"
            image={{ className: "object-contain" }}
            value={
              mobileImage?.id
                ? getDirectusImage(mobileImage.id, {
                    height: mobileHeight,
                    width: mobileWidth,
                    format: "webp",
                  })
                : (mobileColor ?? "")
            }
            mdValue={
              tabletImage?.id
                ? getDirectusImage(tabletImage.id, {
                    height: tabletHeight,
                    width: tabletWidth,
                    format: "webp",
                  })
                : (tabletColor ?? "")
            }
            dValue={
              desktopImage?.id
                ? getDirectusImage(desktopImage.id, {
                    height: desktopHeight,
                    width: desktopWidth,
                    format: "webp",
                  })
                : (desktopColor ?? "")
            }
            border={{ radius: "8px" }}
            m={{ mb: 24 }}
            width={mobileWidth}
            height={mobileHeight}
            md={{ width: tabletWidth, height: tabletHeight }}
            d={{
              m: { my: 0 },
              width: desktopWidth,
              height: desktopHeight,
            }}
          />
        </Box>
      )}
    </Flex>
  );
};
